import React, { useState, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Send, ArrowLeft, Box, UploadCloud, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import { ProjectVisualPicker, ProjectImage } from '../components/projectVisual';

const Projects: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [ifcFile, setIfcFile] = useState<File | null>(null);
  const [image, setImage] = useState<ProjectImage>(null);
  const [loading, setLoading] = useState(false);
  const [modalConfig, setModalConfig] = useState<any>({ isOpen: false, title: '', message: '', type: 'info' });
  const ifcInputRef = useRef<HTMLInputElement>(null);

  const handleSelectIfc = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.ifc')) {
      setModalConfig({ isOpen: true, title: 'Ficheiro inválido', message: 'O modelo tem de ter extensão .ifc', type: 'danger' });
      if (ifcInputRef.current) ifcInputRef.current.value = '';
      return;
    }
    setIfcFile(file);
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Fase 1: criar o projeto (devolve o id necessário para o upload do IFC).
      const { data: created } = await api.post('/projects', {
        name,
        description,
        data_inicio: dataInicio || null,
        data_fim: dataFim || null,
        image: image || null,
      });

      // Fase 2: se foi selecionado um IFC, carregá-lo para o projeto recém-criado.
      let ifcWarning = '';
      if (ifcFile && created?.id) {
        try {
          const formData = new FormData();
          formData.append('file', ifcFile);
          await api.post(`/projects/${created.id}/ifc`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (ifcErr) {
          ifcWarning = ' (O modelo IFC não pôde ser carregado; podes tentar novamente na página do projeto.)';
        }
      }

      setModalConfig({
        isOpen: true,
        title: 'Projeto Criado',
        message: 'O projeto foi registado e já se encontra na tua dashboard.' + ifcWarning,
        type: ifcWarning ? 'info' : 'success',
        onConfirm: () => navigate('/dashboard')
      });
      setName('');
      setDescription('');
      setDataInicio('');
      setDataFim('');
      setIfcFile(null);
      setImage(null);
      if (ifcInputRef.current) ifcInputRef.current.value = '';
    } catch (err) {
      setModalConfig({
        isOpen: true,
        title: 'Erro',
        message: 'Não foi possível criar o projeto.',
        type: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 py-10 animate-in fade-in duration-300">
      <Modal
        {...modalConfig}
        onClose={() => {
            setModalConfig({ ...modalConfig, isOpen: false });
            if (modalConfig.type === 'success') navigate('/dashboard');
        }}
        cancelText="Fechar"
      />

      <div>
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors text-sm font-medium mb-6">
          <ArrowLeft size={16} /> <span>Voltar ao dashboard</span>
        </button>
        <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 mb-2">Novo projeto</p>
        <h2 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">Criar um projeto</h2>
        <p className="text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">Preenche os dados base do projeto. Podes adicionar sensores e mais documentos depois de o criar.</p>
      </div>

      <div className="bg-white dark:bg-slate-800 p-8 sm:p-10 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm transition-colors">
        <form onSubmit={handleCreateProject} className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
            {/* Coluna esquerda */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Nome Identificador</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all font-bold text-slate-700 dark:text-white"
                  placeholder="Ex: Mosteiro de Alcobaça"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Descrição Técnica</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full p-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all min-h-[110px] leading-relaxed text-slate-600 dark:text-slate-300 font-medium"
                  placeholder="Detalhes sobre a intervenção..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Data de Início</label>
                  <input
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="w-full p-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all font-bold text-slate-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Prevista de Fim</label>
                  <input
                    type="date"
                    value={dataFim}
                    min={dataInicio || undefined}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="w-full p-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all font-bold text-slate-700 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Coluna direita */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Imagem ou Ícone</label>
                <ProjectVisualPicker
                  value={image}
                  onChange={setImage}
                  onError={(message) => setModalConfig({ isOpen: true, title: 'Erro', message, type: 'danger' })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Modelo IFC <span className="text-slate-300 dark:text-slate-600">(opcional)</span></label>
                {ifcFile ? (
                  <div className="flex items-center justify-between gap-3 p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-3 bg-indigo-600 text-white rounded-2xl shrink-0"><Box size={18} /></div>
                      <p className="text-[11px] font-black text-slate-900 dark:text-white uppercase tracking-tight truncate">{ifcFile.name}</p>
                    </div>
                    <button type="button" onClick={() => { setIfcFile(null); if (ifcInputRef.current) ifcInputRef.current.value = ''; }} className="p-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors shrink-0">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => ifcInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl p-5 text-center hover:border-indigo-500/50 transition-all"
                  >
                    <div className="w-10 h-10 bg-indigo-600/10 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-2">
                      <UploadCloud size={18} />
                    </div>
                    <p className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-tight">Carregar ficheiro IFC</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Apenas .ifc</p>
                  </button>
                )}
                <input ref={ifcInputRef} type="file" accept=".ifc" className="hidden" onChange={handleSelectIfc} />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100 dark:border-slate-700/50">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-5 py-3 text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-slate-900 dark:bg-blue-600 text-white font-semibold text-sm py-3 px-6 rounded-xl hover:bg-black dark:hover:bg-blue-700 transition-all disabled:opacity-60"
            >
              {!loading && <Send size={16} />}
              <span>{loading ? 'A criar…' : 'Criar projeto'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Projects;
